<template>
  <v-dialog 
    v-model="show" 
    width="500"
  >
    <v-card 
      class="modal-limit" 
      flat
    >
      <v-toolbar 
        color="primary" 
        dark 
        flat
      >
        <v-toolbar-title>Benefícios</v-toolbar-title>
        <v-spacer />
        <v-btn 
          @click="close()" 
          icon
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <h3 class="gray--text">
        {{ currentTitle }}
      </h3>
      <div class="limit-msg">
        <span 
          v-if="!hasBalance" 
          class="red--text pl-3 pb-3"
        >
          * Você não possui saldo suficiente para realizar esse resgate.
        </span>
      </div>
      <div v-if="rewardDetails">
        <v-flex 
          v-for="(item, index) in itemsToRedeem" 
          :key="index" 
          class="modal-limit-area"
        >
          <div 
            :class="item.quantity > 0 && hasBalance ? 'active-border': ''"
            class="content" 
          >
            <div class="first-item">
              <img 
                :src="item.img" 
                width="30" 
                class="mr-2" 
              >
              <h4 
                :class="{ active: !(item.cost > currentBalanceNumber) }"
                class="mr-2" 
              >
                {{ item.title }}
              </h4>
              <span class="trigger">
                {{ item.trigger }}
              </span>
            </div>
            <AppInputPlusMinus
              v-model="model[item.title]"
              :disabled="item.cost > currentBalanceNumber || !hasBalance"
              @plus-value="plusQuantity(item)"
              @minus-value="minusQuantity(item)"
            />
          </div>
          <span 
            v-if="item.cost > currentBalanceNumber" 
            class="message"
          >
            Compre mais com a UPL para liberar este benefício
          </span>
        </v-flex>
      </div>

      <PreviewRewards 
        v-else 
        :items="itemsToRedeem" 
      />

      <div class="btn-container mt-3 pb-3">
        <v-btn
          :disabled="disabledAction || !hasBalance"
          @click="nextStep"
          class="white--text"
          color="primary"
        >
          {{ currentStep.button }}
        </v-btn>
        <div class="info-message">
          <span
            class="gray--text"
          >
            *imagens meramente ilustrativas
          </span>
          <span
            class="gray--text"
          >
            Os valores podem variar, de acordo com entrega
          </span>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import AppInputPlusMinus from '@/components/ui/inputs/AppInputPlusMinus.vue'
import PreviewRewards from './PreviewRewards.vue'

import { rescueCampaignRewards } from '@/services/containers/partners/campaigns.js'

export default {
  name: 'RewardsRescueModal',

  components: {
    AppInputPlusMinus,
    PreviewRewards
  },

  props: {
    items: {
      type: Array,
      default: () => []
    },

    balance: {
      type: Number,
      default: () => 0
    },
    rescueType: {
      type: String,
      default: ''
    },
  },

  data: () => ({
    model: {},
    show: false,
    step: 'rescue-reward',
    steps: [
      {
        button: 'Resgatar benefício',
        value: 'rescue-reward'
      },
      {
        button: 'Confirmar Resgate',
        value: 'confirm-rescue-reward'
      }
    ],
    rewardDetails: true,
    totalValueTriggers: 0,
    disabledAction: true
  }),

  watch: {
    itemsToRedeem: function(value) {
      this.disabledAction = value.every(e => e.quantity === 0)
    },

    model: {
      deep: true,
      handler: function() {
        const totalRewards = this.itemsToRedeem.map(item => item.totalRewardAmount)
        const sumTotalRewards = totalRewards.reduce((acc, curr) => acc + curr, 0)

        this.totalValueTriggers = sumTotalRewards
      }
    }
  },

  computed: {
    currentStep() {
      return this.steps.find(step => step.value === this.step)
    },

    hasBalance() {
      if (this.balance === 0) return true
      return this.balance >= this.totalValueTriggers
    },

    currentBalance() {
      const isCoin = this.rescueType === 'coin'
      const toFixed = isCoin ? 0 : 1
     
      const result = this.balance - this.totalValueTriggers
      return result < 0 ? '0.00' : this.$currencyFormat(result, toFixed)
    },

    currentBalanceNumber() {
      return this.balance - this.totalValueTriggers
    },

    currentTitle() {
      return this.rewardDetails
        ? `Disponível para resgate ${this.currentBalance}${this.rescueType === 'coin' ? ' SDZ' : 'x'}`
        : 'Confira os itens do seu resgate'
    },

    itemsToRedeem() {
      return this.items.map(item => ({
        ...item,
        quantity: this.model[item.title] || 0,
        totalRewardAmount: this.model[item.title] * item.cost || 0,
      }))
    }
  },

  methods: {
    async nextStep() {
      const {
        currentStep: { value }
      } = this

      if (value === 'rescue-reward') {
        this.rewardDetails = false
        this.step = 'confirm-rescue-reward'
        return
      }

      const campaign = this.$route.params.id
      const itemsWithQuantity = this.itemsToRedeem.filter(item => item.quantity > 0)
      const products = [
        ...itemsWithQuantity.map(item => ({
          productName: item.title,
          productValue: Number(item.cost),
          productQuantity: Number(item.quantity),
          _id: item.product._id,
          partner: item.product.partner
        }))
      ]
      try {
        this.$showLoading()
        const res = await rescueCampaignRewards({ campaign, products })
        this.show = false
        this.model = {}
        this.$toast(res.data.message || 'Solicitação de resgate concluída com sucesso!', 'green')
        this.$emit('finalizing-rewards-rescue')
      } catch (err) {
        this.$toast(err.response.data.message, 'red')
      } finally {
        this.$hideLoading()
      }
    },

    open() {
      this.show = true
      this.rewardDetails = true
    },

    close() {
      this.show = false
      this.step = 'rescue-reward'
    },

    plusQuantity(item) {
      let currentValue = Number(this.model[item.title]) || 0
      this.model = {
        ...this.model,
        [item.title]: (currentValue += 1)
      }
    },

    minusQuantity(item) {
      let currentValue = Number(this.model[item.title]) || 0
      this.model = {
        ...this.model,
        [item.title]: (currentValue -= 1)
      }
    }
  }
}
</script>

<style lang="stylus">
.modal-limit
  &-area
    display: flex;
    flex-direction: column;
    margin: 10px 15px 0 15px;
    .content
      display: flex;
      justify-content: space-between;
      padding: 10px 20px;
      border: 1px solid #E0E0E0;
      border-radius: 5px;
      .first-item
        display: flex;
        justify-content: center;
        align-items: center;
        @media (max-width: 768px)
          flex-direction: column;
          align-items: center;
          text-align: center;
      h4
        max-width: 120px;
        min-width: 120px;
        color: #CCCCCC;
      .active
        color: #3A7B80;
    .active-border
      border: 1px solid #299d8d;
    .trigger
      background-color: #299D8D;
      color: #fff;
      border-radius: 20px;
      padding: 0 10px;
      @media (max-width: 768px)
        margin-top: 5px;
    .message
      color: #CCCCCC;
      font-size: 11px;
  .btn-container
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 20px;
    .info-message
      display: flex;
      flex-direction: column;
      align-items: center;
      font-size: 12px;
  h3
    padding: 20px;
    font-weight: 600;
    font-size: 20px;
  .limit-msg
    min-height: 25px;
</style>
